import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Auth, Amplify } from 'aws-amplify'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import config from 'amplifyconfiguration'
import { useTranslation } from 'react-i18next'
import small_logo from 'components/assets/small_logo.svg'

import { useTheme } from '@mui/styles'
import {
  AppBar,
  Chip,
  Toolbar,
  Grid,
  Box,
  IconButton,
  MenuItem,
  Typography,
  Menu,
  Badge,
  useMediaQuery,
  Modal,
  Fade,
  Avatar,
  ListItemIcon,
  Autocomplete,
  TextField,
} from '@mui/material'
import {
  Add as AddIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  LiveHelp as LiveHelpIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  AttachMoney as AttachMoneyIcon,
  MenuOpen as MenuOpenIcon,
  SwitchCamera,
  SchoolRounded,
  Language,
  ViewModule,
  ViewList,
} from '@mui/icons-material'

import SelectButton from 'components/generic/buttons/SelectButton'
import UnstyledLink from 'components/generic/links'
import RouterBreadcrumbs from 'components/generic/breadcrumbs'

import { showFormModal, toggleMobileMenuVisible, toggleOrgMode } from 'state/ui'
import {
  transformYardList,
  USER_SELECTED_YARD_LOCAL_STORAGE_KEY,
  USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY,
  clearLocalOrgInfo,
} from 'state/auth'
import { ORG_MODE_LIST_OBJECT } from 'utils/ui/actions'
import FORM_MODALS from 'utils/ui/modals'
import { clearReduxFormState } from 'utils/helpers'

import ChatComponentGemini from './ChatComponentGemini'

Amplify.configure(config)

const CustomIconButton = styled(IconButton)`
  height: 42px;
`

const ProfileMenu = ({
  anchorEl,
  id,
  isMenuOpen,
  handleMenuClose,
  toggleTheme,
  currentTheme,
  toggleNavStyle,
  useCategoryNav,
}) => {
  const { canToggleLanguage } = useSelector(state => state.featureFlags?.variables || {})

  const onClickToggleTheme = () => {
    handleMenuClose()
    toggleTheme()
  }

  const onClickLogout = async () => {
    await Auth.signOut()
    handleMenuClose()
    clearLocalOrgInfo()
    window.location.reload()
  }

  const { t, i18n } = useTranslation('inboundTicket')

  const changeLanguage = lang => {
    i18n.changeLanguage(lang)
  }

  const currentLanguage = i18n.language

  return (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      id={id}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem
        onClick={() => {
          toggleNavStyle()
          handleMenuClose()
        }}
      >
        <ListItemIcon>{useCategoryNav ? <ViewModule fontSize="small" /> : <ViewList fontSize="small" />}</ListItemIcon>
        {`Switch to ${useCategoryNav ? 'Classic' : 'New'} Navigation`}
      </MenuItem>
      <MenuItem onClick={onClickToggleTheme}>
        <ListItemIcon>
          {currentTheme === 'dark' ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
        </ListItemIcon>
        {currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      </MenuItem>
      {canToggleLanguage && (
        <MenuItem onClick={() => changeLanguage(currentLanguage === 'en' ? 'es' : 'en')}>
          <ListItemIcon>
            <Language fontSize="small" />
          </ListItemIcon>
          {currentLanguage === 'en' ? t('language_spanish') : t('language_english')}
        </MenuItem>
      )}
      <MenuItem onClick={() => window.open('https://www.greensparksoftware.com/refer-someone', '_blank')}>
        <ListItemIcon>
          <AttachMoneyIcon fontSize="small" />
        </ListItemIcon>
        Refer a Friend!
      </MenuItem>
      <MenuItem onClick={onClickLogout}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  )
}

ProfileMenu.propTypes = {
  anchorEl: PropTypes.oneOfType([PropTypes.instanceOf(HTMLElement), PropTypes.oneOf([null])]),
  id: PropTypes.string.isRequired,
  isMenuOpen: PropTypes.bool.isRequired,
  handleMenuClose: PropTypes.func.isRequired,
  toggleTheme: PropTypes.func.isRequired,
  currentTheme: PropTypes.string.isRequired,
  toggleNavStyle: PropTypes.func.isRequired,
  useCategoryNav: PropTypes.bool.isRequired,
}

ProfileMenu.defaultProps = {
  anchorEl: null,
}

const CustomAppBar = ({
  showNotifications,
  showLocationSelect,
  showSettings,
  showSupport,
  showAccountActions,
  showEquipment,
  title,
  isSettings,
  toggleSideMenuText,
  toggleTheme,
  currentTheme,
  showSideMenuText,
  toggleNavStyle,
  useCategoryNav,
  accessibleMenuOptions,
}) => {
  const { organization, yardDetailsList, selectedYard } = useSelector(state => state.auth)
  const { useMinimalObt, gsChatTreatment, appRouteLoadBoard, orgLevelReports } = useSelector(
    state => state.featureFlags?.variables || {},
  )

  const [yardsList, setYardsList] = useState([])
  const { inOrgMode } = useSelector(state => state.ui)
  const { user } = useSelector(state => state.auth)
  const theme = useTheme()
  const isMobile = useMediaQuery(query => query.breakpoints.down('sm'))
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const menuId = 'primary-app-bar-menu'
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    if (yardDetailsList) {
      const newYardsList = transformYardList(organization, yardDetailsList)
      const orgModeEnabled = user?.attributes?.['custom:orgModeEnabled'] === 'true'

      if (orgModeEnabled && (appRouteLoadBoard || orgLevelReports)) {
        newYardsList.push({
          id: ORG_MODE_LIST_OBJECT.id,
          name: organization.charAt(0).toUpperCase() + organization.slice(1) + ORG_MODE_LIST_OBJECT.name,
        })
      }

      setYardsList(newYardsList)
    }
  }, [organization, yardDetailsList, user, appRouteLoadBoard, orgLevelReports])

  const isMenuOpen = Boolean(anchorEl)

  const defaultYardValue = inOrgMode
    ? ORG_MODE_LIST_OBJECT.id
    : yardDetailsList?.find(yard => yard?.name === selectedYard)?.id || ''

  const handleOptionClick = option => {
    clearReduxFormState(dispatch)

    if (option && option.action) {
      navigate(option.action)
    }
  }

  const handleProfileMenuOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleModalOpen = () => {
    setModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
  }

  const handleHamburgerClick = () => (isMobile ? dispatch(toggleMobileMenuVisible()) : toggleSideMenuText())

  return (
    <>
      <AppBar
        id="app-bar-header"
        position="static"
        sx={{
          background: theme.palette.background.default,
          color: theme.palette.primary.actionMain,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <Grid container justifyContent="space-between" alignItems="center" flexWrap="nowrap">
            <Grid
              container
              item
              alignItems="center"
              sx={{
                width: 'auto',
                padding: `${theme.spacing(0)} 6px ${theme.spacing(0)} 6px`,
              }}
            >
              {isSettings ? (
                <>
                  <Grid item sx={{ mr: 2 }}>
                    <IconButton
                      onClick={() => {
                        navigate(useMinimalObt ? '/app/sales' : '/app/purchases')
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                  <Typography color="textPrimary">Settings</Typography>
                </>
              ) : (
                <>
                  <Grid item sx={{ mr: 2 }}>
                    <IconButton edge="start" aria-label="menu" onClick={handleHamburgerClick}>
                      {isMobile ? <MenuIcon /> : showSideMenuText ? <MenuOpenIcon /> : <MenuIcon />}
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <RouterBreadcrumbs
                      accessibleMenuOptions={accessibleMenuOptions}
                      handleOptionClick={handleOptionClick}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {title && (
              <Grid item>
                <Typography variant="h6">{title}</Typography>
              </Grid>
            )}

            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {showLocationSelect && yardsList.length > 0 && (
                  <Autocomplete
                    size="small"
                    options={yardsList}
                    disableClearable
                    sx={{ mr: 2, minWidth: 160 }}
                    getOptionLabel={option => option.name}
                    value={yardsList.find(yard => yard.id === defaultYardValue) || yardsList[0]}
                    onChange={(event, newValue) => {
                      if (newValue) {
                        const selectedId = newValue.id
                        if (inOrgMode && selectedId !== ORG_MODE_LIST_OBJECT.id) {
                          dispatch(toggleOrgMode({ sub: user.attributes.sub }))
                        } else if (!inOrgMode && selectedId === ORG_MODE_LIST_OBJECT.id) {
                          dispatch(toggleOrgMode({ sub: user.attributes.sub }))
                          if (appRouteLoadBoard) {
                            if (window.location.href.includes('/app/settings/')) {
                              navigate('/app/settings/dispatch/drivers')
                            } else {
                              navigate('/app/load-board/active-tickets')
                            }
                          } else if (orgLevelReports) {
                            navigate('/app/reports/standard')
                          }
                        }

                        if (selectedId !== ORG_MODE_LIST_OBJECT.id) {
                          const selectedYardName = yardDetailsList?.find(yard => yard.id === selectedId)?.name
                          window.localStorage.setItem(USER_SELECTED_YARD_LOCAL_STORAGE_KEY, selectedYardName)
                          window.localStorage.setItem(USER_SELECTED_YARD_ID_LOCAL_STORAGE_KEY, selectedId)
                        }
                        window.location.reload()
                      }
                    }}
                    renderInput={params => <TextField {...params} variant="outlined" sx={{ m: 1, minWidth: 120 }} />}
                  />
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {showNotifications && (
                    <CustomIconButton aria-label="show 17 new notifications" color="inherit">
                      <Badge badgeContent={2} color="secondary">
                        <NotificationsIcon />
                      </Badge>
                    </CustomIconButton>
                  )}

                  {showSettings && !isMobile && (
                    <CustomIconButton
                      aria-label="settings"
                      onClick={() => {
                        if (inOrgMode) {
                          navigate('/app/settings/dispatch/drivers')
                        } else {
                          navigate('/app/settings')
                        }
                      }}
                      disabled={inOrgMode && !appRouteLoadBoard}
                    >
                      <SettingsIcon />
                    </CustomIconButton>
                  )}

                  {showEquipment && (
                    <CustomIconButton
                      aria-label="equipment-selector"
                      onClick={() => {
                        dispatch(
                          showFormModal({
                            formModalName: FORM_MODALS.EQUIPMENT_SELECTOR,
                          }),
                        )
                      }}
                    >
                      <SwitchCamera />
                    </CustomIconButton>
                  )}

                  {showSupport && !isMobile && (
                    <>
                      <UnstyledLink
                        external
                        target="_blank"
                        rel="noreferrer"
                        to="https://intercom.help/greenspark-software/en"
                      >
                        <CustomIconButton aria-label="contact-support">
                          <LiveHelpIcon />
                        </CustomIconButton>
                      </UnstyledLink>
                      <UnstyledLink
                        external
                        target="_blank"
                        rel="noreferrer"
                        to="https://greensparksoftware-academy.workramp.io/login"
                      >
                        <CustomIconButton aria-label="greenspark-academy">
                          <SchoolRounded />
                        </CustomIconButton>
                      </UnstyledLink>
                    </>
                  )}
                  {showAccountActions &&
                    (isMobile ? (
                      <IconButton
                        aria-label="account of current user"
                        aria-haspopup="true"
                        onClick={handleProfileMenuOpen}
                      >
                        <PersonIcon />
                      </IconButton>
                    ) : (
                      <Chip
                        aria-label="account of current user"
                        aria-haspopup="true"
                        label={user?.attributes?.email}
                        variant="outlined"
                        onClick={handleProfileMenuOpen}
                        icon={<PersonIcon />}
                        sx={{
                          maxWidth: '200px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      />
                    ))}
                  {gsChatTreatment ? (
                    <Chip
                      label={!isMobile ? 'Chat with Sparky' : 'Sparky'}
                      variant="outlined"
                      onClick={handleModalOpen}
                      avatar={<Avatar alt="Sparky" src={small_logo} />}
                      sx={{
                        ml: 1,
                      }}
                    />
                  ) : null}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>

      <ProfileMenu
        anchorEl={anchorEl}
        id={menuId}
        isMenuOpen={isMenuOpen}
        handleMenuClose={handleMenuClose}
        toggleTheme={toggleTheme}
        currentTheme={currentTheme}
        toggleNavStyle={toggleNavStyle}
        useCategoryNav={useCategoryNav}
      />

      <Modal
        aria-labelledby="custom-modal-title"
        aria-describedby="custom-modal-description"
        open={modalOpen}
        onClose={handleModalClose}
        closeAfterTransition
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Fade in={modalOpen}>
          <Box
            sx={{
              width: '90%',
              maxWidth: '1200px',
              bgcolor: 'background.paper',
              borderRadius: '8px',
              boxShadow: 24,
              p: 2,
              outline: 'none',
            }}
          >
            <ChatComponentGemini />
          </Box>
        </Fade>
      </Modal>
    </>
  )
}

CustomAppBar.propTypes = {
  showNotifications: PropTypes.bool,
  showLocationSelect: PropTypes.bool,
  showSettings: PropTypes.bool,
  showSupport: PropTypes.bool,
  showAccountActions: PropTypes.bool,
  showEquipment: PropTypes.bool,
  title: PropTypes.string,
  isSettings: PropTypes.bool,
  toggleSideMenuText: PropTypes.func,
  toggleTheme: PropTypes.func.isRequired,
  currentTheme: PropTypes.string.isRequired,
  showSideMenuText: PropTypes.bool,
  toggleNavStyle: PropTypes.func,
  useCategoryNav: PropTypes.bool,
  accessibleMenuOptions: PropTypes.array,
}

CustomAppBar.defaultProps = {
  showNotifications: true,
  showLocationSelect: true,
  showSettings: true,
  showSupport: true,
  showAccountActions: true,
  showEquipment: true,
  title: '',
  isSettings: false,
  toggleSideMenuText: () => {},
  showSideMenuText: false,
  toggleNavStyle: () => {},
  useCategoryNav: false,
  accessibleMenuOptions: [],
}

export default CustomAppBar
